// "I think this is installed at …" — the Message Box's reminder about every
// UNASSIGNED device it hears.
//
// Asked for 2026-10-24 (the owner). The console was already showing the line:
//
//   12:38:51 ← 907612/client: WS,1544,56,2 Touch Switch Tuya,…,Optilink,
//                             192.168.10.38,… Unassigned - Smartify_907612
//
// — a device nobody can see in any app, talking, with no way to tell whose it
// is. But it names its network, and the assigned devices on that network name
// the house: 907612 is Nikhil Kulkarni's (location 37, Badlapur). So each one
// heard gets a card that says what it probably is and where it probably lives,
// and an **Assign** button with the traced owner and location already picked.
// "This will help us to assign all unidentified devices."
//
// ⚠️ A GUESS IS SAID AS A GUESS. The sentence is first-person and hedged, the
// evidence is on the card (which signal, how many devices, which rooms), a tie
// is called out, and a factory SSID is marked weak — because "Smartify" and
// "Home" are the SSID of dozens of houses. Nothing is written until somebody
// presses Assign, and the ROOM is never guessed: only the installer knows that.
//
// Rules and ranking: core/devices/siteGuess (pure, tested against this very
// device). The estate index: core/devices/siteGuessIndex (one read per session).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApp } from '../../core/state/AppContext';
import { loadSiteIndex, siteHintFor } from '../../core/devices/siteGuessIndex';
import AssignDeviceButton from './AssignDeviceButton';
import { unosHeard } from '../../core/devices/siteGuess';

const CONF = {
  high: { color: 'success', label: 'same access point' },
  likely: { color: 'info', label: 'same network' },
  weak: { color: 'warning', label: 'weak — shared SSID' },
};

/**
 * `messages` — the console's log (mqttMonitor entries). `onPick` receives a
 * unique number when the operator wants it in the console's own device pane.
 */
export default function UnassignedHintBar({ messages, onPick, max = 4 }) {
  const { isSuperAdmin } = useApp();
  const [ready, setReady] = useState(null);
  const [dismissed, setDismissed] = useState([]);

  const load = useCallback((force) => {
    setReady(null);
    loadSiteIndex({ force }).then((s) => setReady(s)).catch(() => setReady({ ready: false, error: 'failed' }));
  }, []);

  useEffect(() => { if (isSuperAdmin) load(false); }, [isSuperAdmin, load]);

  // The hints follow the log with no state of their own: the console re-renders
  // on new lines and every lookup is a Map hit, so there is nothing to cache.
  if (!isSuperAdmin || !ready) return null;

  // ⚠️ The columns arrive with a backend restart (deviceTracking.js, 2026-10-24).
  // Say so rather than quietly finding nothing for ever.
  if (!ready.ready) {
    return (
      <Alert severity="info" sx={{ mb: 1 }} icon={<TravelExploreIcon fontSize="small" />}>
        Tracing an unassigned device to a house needs the server’s network columns
        (<code>WifiName</code>, <code>DeviceIp</code>, <code>Bssid</code> on <code>GetDeviceTrackingList</code>) —
        restart the backend to switch this on.
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load(true)} sx={{ ml: 1 }}>Re-read</Button>
      </Alert>
    );
  }

  const hints = unosHeard(messages, { limit: 24 })
    .filter((uno) => !dismissed.includes(uno))
    .map((uno) => {
      // The newest line from that device, so the live SSID is used.
      const line = (messages || []).find((m) => String(m.uno) === uno && m.payload);
      return siteHintFor(uno, line && line.payload);
    })
    .filter(Boolean)
    .slice(0, max);

  if (!hints.length) return null;

  return (
    <Box sx={{ mb: 1 }}>
      {hints.map((h) => {
        const g = h.guess;
        const conf = g ? CONF[g.confidence] || CONF.weak : null;
        return (
          <Collapse in key={h.uno}>
            <Paper
              variant="outlined"
              sx={{
                p: 1, mb: 0.75, display: 'flex', gap: 1, alignItems: 'flex-start',
                borderColor: conf ? `${conf.color}.main` : 'divider',
              }}
            >
              <TravelExploreIcon fontSize="small" sx={{ mt: 0.25, color: conf ? `${conf.color}.main` : 'text.secondary' }} />
              <Box sx={{ width: 0, flex: '1 1 0', minWidth: 220 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={700}>{h.uno}</Typography>
                  <Chip size="small" color="default" variant="outlined" label="unassigned" />
                  {conf && <Chip size="small" color={conf.color} variant="outlined" label={conf.label} />}
                  {!h.row && <Chip size="small" color="warning" variant="outlined" label="no row on the server" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  {g ? g.text : `Nothing on its network matches a known site${h.subject?.WifiName ? ` (SSID “${h.subject.WifiName}”)` : ' — it has not reported an SSID'}.`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {onPick && (
                  <Tooltip title="Put this number in the console">
                    <Button size="small" onClick={() => onPick(h.uno)}>Open</Button>
                  </Tooltip>
                )}
                {/* The traced owner and location are PRE-PICKED; the room and
                    Save stay the operator's. */}
                <AssignFromHint hint={h} onDone={() => load(true)} />
                <Tooltip title="Hide until it speaks again next session">
                  <IconButton size="small" onClick={() => setDismissed((d) => [...d, h.uno])}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          </Collapse>
        );
      })}
    </Box>
  );
}

// Split out so the assign dialog is mounted per card with its own suggestion.
function AssignFromHint({ hint, onDone }) {
  const best = hint.guess && hint.guess.detail;
  return (
    <AssignDeviceButton
      uniqueNumber={hint.uno}
      onDone={onDone}
      suggest={best ? { userId: best.ownerUserId, locationId: best.locationId } : null}
    />
  );
}
